import { type ECommAdvancedFeatureStates } from '@choco-admin/types';
import { useCallback } from 'react';

import {
  SupplierECommAdvancedSettingsFragmentFragmentDoc,
  type SupplierECommAdvancedSettingsFragmentFragment,
  useSuppliersPageECommAdvancedSettingsUpdateMutation,
} from '../../../generated';

const SAVE_ERROR_MESSAGE = 'Could not change the feature.';

type ECommAdvancedSettings = NonNullable<
  SupplierECommAdvancedSettingsFragmentFragment['eCommAdvancedSettings']
>;

export type ECommAdvancedSaveResult =
  | { kind: 'success' }
  | { kind: 'error'; message: string };

export interface UseUpdateECommAdvancedSettingsResult {
  /** Switches the features given. The ones left out keep their value. */
  setFeatures: (
    changes: Partial<ECommAdvancedFeatureStates>
  ) => Promise<ECommAdvancedSaveResult>;
  /** A write is in flight. Every control must wait for it. */
  isSaving: boolean;
}

const toSettings = (
  features: ECommAdvancedFeatureStates
): ECommAdvancedSettings => ({
  __typename: 'ECommAdvancedSettings',
  searchSynonyms: {
    __typename: 'ECommAdvancedFeatureSetting',
    enabled: features.searchSynonyms,
  },
  productBoosting: {
    __typename: 'ECommAdvancedFeatureSetting',
    enabled: features.productBoosting,
  },
});

/**
 * Writes one distributor's eCommerce Advanced features. Each write applies
 * immediately, outside the form's Save.
 *
 * Call it from a component that outlives the Settings tab (`SupplierForm`), not
 * from the tab. HeroUI unmounts an inactive tab, and `isSaving` belongs to
 * whichever component holds this hook: held by the tab, it would be lost on a
 * round trip to another tab while the first write was still in flight, and a
 * second write could then overlap it. The later of two overlapping responses
 * wins in the cache, so a slow first response would overwrite the second
 * write's newer settings.
 *
 * The settings are read with the rest of the distributor by
 * `GetSupplierDetailsData`, so a write lands in that query's `Supplier` entry
 * rather than triggering a refetch of the whole distributor. The result carries
 * every feature, so it replaces the cached settings whole.
 *
 * A switch moves as soon as it is pressed: the write goes out with an optimistic
 * result, and Apollo drops that layer when the real response arrives. A refused
 * write never reaches the cache, so the switch goes back by itself.
 *
 * @param features What is stored now, so the optimistic result can carry over
 *   the features this write leaves out. Absent while creating a distributor, and
 *   null when they could not be read; nothing can be written in either case.
 */
export const useUpdateECommAdvancedSettings = (
  supplierId?: string,
  features?: ECommAdvancedFeatureStates | null
): UseUpdateECommAdvancedSettingsResult => {
  const [mutate, { loading: isSaving }] =
    useSuppliersPageECommAdvancedSettingsUpdateMutation();

  const setFeatures = useCallback(
    async (
      changes: Partial<ECommAdvancedFeatureStates>
    ): Promise<ECommAdvancedSaveResult> => {
      if (!supplierId || !features) {
        return { kind: 'error', message: SAVE_ERROR_MESSAGE };
      }

      try {
        const response = await mutate({
          variables: {
            input: {
              supplierId,
              searchSynonymsEnabled: changes.searchSynonyms,
              productBoostingEnabled: changes.productBoosting,
            },
          },
          optimisticResponse: {
            supplierECommAdvancedSettingsUpdate: {
              __typename: 'SupplierECommAdvancedSettingsUpdateResult',
              settings: toSettings({ ...features, ...changes }),
            },
          },
          update: (cache, { data }) => {
            const result = data?.supplierECommAdvancedSettingsUpdate;
            if (
              result?.__typename !== 'SupplierECommAdvancedSettingsUpdateResult'
            ) {
              return;
            }

            cache.writeFragment({
              id: cache.identify({ __typename: 'Supplier', id: supplierId }),
              fragment: SupplierECommAdvancedSettingsFragmentFragmentDoc,
              data: {
                __typename: 'Supplier',
                eCommAdvancedSettings: result.settings,
              },
            });
          },
        });
        const result = response.data?.supplierECommAdvancedSettingsUpdate;

        if (
          result?.__typename === 'SupplierECommAdvancedSettingsUpdateResult'
        ) {
          return { kind: 'success' };
        }

        // Mutations resolve on GraphQL errors (errorPolicy 'all'), so a
        // rejected call arrives here with no payload rather than as a throw.
        // eslint-disable-next-line no-console
        console.error(
          'Could not change the eCommerce Advanced features:',
          result ? result.message : response.errors?.[0]?.message
        );

        return {
          kind: 'error',
          message:
            result?.__typename === 'AuthorizationFailure'
              ? 'Only Choco admins can change these features.'
              : SAVE_ERROR_MESSAGE,
        };
      } catch (caught) {
        // eslint-disable-next-line no-console
        console.error(
          'Could not change the eCommerce Advanced features:',
          caught
        );
        return { kind: 'error', message: SAVE_ERROR_MESSAGE };
      }
    },
    [features, mutate, supplierId]
  );

  return { setFeatures, isSaving };
};
